import {
  clearAppStateForLocalStorage,
  getDefaultAppState,
} from "@excalidraw/excalidraw/appState";
import { clearElementsForLocalStorage } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState } from "@excalidraw/excalidraw/types";

import { STORAGE_KEYS } from "../app_constants";

// Per-board localStorage keys. When the app is opened for a specific document
// (a note's whiteboard / a shared sketch, via `?documentId=`), its canvas is
// cached under a documentId-scoped key so distinct boards never share — and so
// can never bleed into — a single cache. Without this, opening board B while
// board A sits in another tab would surface A's drawing (both wrote the same
// global "excalidraw" key). Falls back to the global keys for plain
// (no-documentId) excalidraw usage.
const getActiveDocumentId = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return (window as any).currentDocumentId || null;
};

export const getElementsStorageKey = () => {
  const id = getActiveDocumentId();
  return id
    ? `${STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS}:${id}`
    : STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS;
};

export const getAppStateStorageKey = () => {
  const id = getActiveDocumentId();
  return id
    ? `${STORAGE_KEYS.LOCAL_STORAGE_APP_STATE}:${id}`
    : STORAGE_KEYS.LOCAL_STORAGE_APP_STATE;
};

export const saveUsernameToLocalStorage = (username: string) => {
  try {
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_COLLAB,
      JSON.stringify({ username }),
    );
  } catch (error: any) {
    // Unable to access window.localStorage
    console.error(error);
  }
};

export const importUsernameFromLocalStorage = (): string | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_COLLAB);
    if (data) {
      return JSON.parse(data).username;
    }
  } catch (error: any) {
    // Unable to access localStorage
    console.error(error);
  }

  return null;
};

export const importFromLocalStorage = () => {
  let savedElements = null;
  let savedState = null;

  try {
    savedElements = localStorage.getItem(getElementsStorageKey());
    savedState = localStorage.getItem(getAppStateStorageKey());
  } catch (error: any) {
    // Unable to access localStorage
    console.error(error);
  }

  let elements: ExcalidrawElement[] = [];
  if (savedElements) {
    try {
      elements = clearElementsForLocalStorage(JSON.parse(savedElements));
    } catch (error: any) {
      console.error(error);
      // Do nothing because elements array is already empty
    }
  }

  let appState = null;
  if (savedState) {
    try {
      appState = {
        ...getDefaultAppState(),
        ...clearAppStateForLocalStorage(
          JSON.parse(savedState) as Partial<AppState>,
        ),
      };
    } catch (error: any) {
      console.error(error);
      // Do nothing because appState is already null
    }
  }
  return { elements, appState };
};

export const getElementsStorageSize = () => {
  try {
    const elements = localStorage.getItem(getElementsStorageKey());
    const elementsSize = elements?.length || 0;
    return elementsSize;
  } catch (error: any) {
    console.error(error);
    return 0;
  }
};

export const getTotalStorageSize = () => {
  try {
    const appState = localStorage.getItem(getAppStateStorageKey());
    const collab = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_COLLAB);

    const appStateSize = appState?.length || 0;
    const collabSize = collab?.length || 0;

    return appStateSize + collabSize + getElementsStorageSize();
  } catch (error: any) {
    console.error(error);
    return 0;
  }
};
